import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { createToonGradientMap, toonMat } from './materials.js';

const ENTER_RANGE = 3.4;

export type VehicleKind = 'quad' | 'truck';

// ── Boost pad spawn positions (exported so FPSGame can read them if needed) ──
export const BOOST_PAD_POSITIONS: Array<{ x: number; z: number }> = [
  { x: 5,   z: -10 },
  { x: -18, z: 20  },
  { x: 30,  z: -5  },
  { x: -5,  z: 35  },
];

interface BoostPad {
  mesh: THREE.Mesh;
  x: number;
  z: number;
  cooldown: number; // seconds before this pad re-triggers
}

interface ParticleBurst {
  meshes: THREE.Mesh[];
  velocities: THREE.Vector3[];
  life: number; // remaining seconds
}

interface Vehicle {
  id: number;
  kind: VehicleKind;
  group: THREE.Group;
  body: CANNON.Body;
  wheels: THREE.Mesh[];
  speed: number;
  yaw: number;
  health: number;
  maxHealth: number;
  headlightMeshes: THREE.Mesh[];
  hornFlash: number;
  /** Pre-computed mount points for camera/seat */
  seatLocal: THREE.Vector3;
  /** Fuel 0–100 */
  fuel: number;
  /** Whether the vehicle has already exploded */
  exploded: boolean;
  /** Remaining boost-pad impulse time */
  boostPadTimer: number;
}

export interface VehicleControls {
  forward: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
  boost: boolean;
  brake: boolean;
}

/** Simple arcade vehicle system: drivable quads and trucks with physics body. */
export class FpsVehicles {
  private vehicles: Vehicle[] = [];
  private nextId = 1;
  private grad: THREE.DataTexture;
  /** Vehicle currently driven by player, or null. */
  private playerInVehicleId: number | null = null;
  private hornCd = 0;

  // Boost pad scene props
  private boostPads: BoostPad[] = [];

  // Active particle bursts (explosions)
  private bursts: ParticleBurst[] = [];

  // Fuel HUD element
  private fuelHud: HTMLDivElement | null = null;

  constructor(
    private scene: THREE.Scene,
    private sceneRoot: THREE.Group,
    private world: CANNON.World,
    private floorMat: CANNON.Material,
    spawns: { x: number; z: number; kind: VehicleKind; hue?: number }[],
  ) {
    this.grad = createToonGradientMap();
    for (const s of spawns) {
      this.spawn(s.x, s.z, s.kind, s.hue ?? Math.random());
    }
    this.initBoostPads();
    this.initFuelHud();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Fuel HUD
  // ─────────────────────────────────────────────────────────────────────────────

  private initFuelHud(): void {
    const el = document.createElement('div');
    el.id = 'fps-fuel-hud';
    el.style.cssText = [
      'position:fixed',
      'bottom:90px',
      'right:24px',
      'width:120px',
      'background:rgba(0,0,0,0.55)',
      'border:1px solid rgba(255,255,255,0.18)',
      'border-radius:6px',
      'padding:5px 8px',
      'font-family:monospace',
      'font-size:12px',
      'color:#ffe',
      'pointer-events:none',
      'display:none',
      'z-index:300',
    ].join(';');
    document.body.appendChild(el);
    this.fuelHud = el;
  }

  private updateFuelHud(fuel: number): void {
    if (!this.fuelHud) return;
    const pct = Math.max(0, Math.min(100, fuel));
    const color = pct > 50 ? '#4eff91' : pct > 20 ? '#ffdd44' : '#ff4444';
    this.fuelHud.innerHTML =
      `<div style="margin-bottom:3px;opacity:0.8">FUEL</div>` +
      `<div style="background:#222;border-radius:3px;height:8px;width:100%">` +
      `<div style="width:${pct}%;height:8px;border-radius:3px;background:${color};transition:width 0.1s"></div>` +
      `</div><div style="text-align:right;margin-top:2px">${pct.toFixed(0)}%</div>`;
    this.fuelHud.style.display = 'block';
  }

  private hideFuelHud(): void {
    if (this.fuelHud) this.fuelHud.style.display = 'none';
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Boost Pads
  // ─────────────────────────────────────────────────────────────────────────────

  private initBoostPads(): void {
    const padGeo = new THREE.CylinderGeometry(1.1, 1.1, 0.08, 20);
    const padMat = new THREE.MeshStandardMaterial({
      color: 0xffdd00,
      emissive: 0xffaa00,
      emissiveIntensity: 0.6,
      roughness: 0.4,
      metalness: 0.3,
    });
    // Arrow stripe on top
    const arrowGeo = new THREE.ConeGeometry(0.4, 0.7, 8);
    const arrowMat = new THREE.MeshStandardMaterial({
      color: 0xff8800,
      emissive: 0xff5500,
      emissiveIntensity: 0.9,
    });

    for (const pos of BOOST_PAD_POSITIONS) {
      const group = new THREE.Group();
      group.position.set(pos.x, 0.04, pos.z);

      const disk = new THREE.Mesh(padGeo, padMat);
      disk.receiveShadow = true;
      group.add(disk);

      const arrow = new THREE.Mesh(arrowGeo, arrowMat);
      arrow.rotation.x = -Math.PI / 2; // point forward (-Z)
      arrow.position.set(0, 0.1, -0.2);
      group.add(arrow);

      this.sceneRoot.add(group);
      this.boostPads.push({ mesh: group as unknown as THREE.Mesh, x: pos.x, z: pos.z, cooldown: 0 });
    }
  }

  private checkBoostPads(v: Vehicle, dt: number): void {
    const vx = v.body.position.x;
    const vz = v.body.position.z;
    for (const pad of this.boostPads) {
      pad.cooldown = Math.max(0, pad.cooldown - dt);
      if (pad.cooldown > 0) continue;
      const dist = Math.hypot(vx - pad.x, vz - pad.z);
      if (dist < 1.3) {
        // Trigger boost: +15 m/s forward impulse
        const fwd = new THREE.Vector3(-Math.sin(v.yaw), 0, -Math.cos(v.yaw));
        v.body.velocity.x += fwd.x * 15;
        v.body.velocity.z += fwd.z * 15;
        v.boostPadTimer = 1.5;
        pad.cooldown = 3.0; // 3s before pad reactivates

        // Visual flash: pad turns white briefly
        const meshGroup = pad.mesh as unknown as THREE.Group;
        const diskMesh = meshGroup.children[0] as THREE.Mesh;
        const mat = diskMesh.material as THREE.MeshStandardMaterial;
        mat.emissive.set(0xffffff);
        // Reset colour after short delay
        setTimeout(() => { mat.emissive.set(0xffaa00); }, 300);
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Explosion
  // ─────────────────────────────────────────────────────────────────────────────

  private explodeVehicle(v: Vehicle): void {
    v.exploded = true;
    // Hide vehicle mesh
    v.group.visible = false;
    // Disable physics body
    v.body.velocity.set(0, 0, 0);
    v.body.type = CANNON.Body.STATIC;

    // If player was in this vehicle, nothing special — FPSGame's health check
    // will handle it (health <= 0 check is exposed via getCurrentHealth).

    // Spawn particle burst: 12 spheres gold+orange
    const colors = [0xffcc00, 0xff7700, 0xff3300, 0xffee55];
    const meshes: THREE.Mesh[] = [];
    const velocities: THREE.Vector3[] = [];
    for (let i = 0; i < 12; i++) {
      const r = 0.12 + Math.random() * 0.18;
      const geo = new THREE.SphereGeometry(r, 6, 5);
      const mat = new THREE.MeshBasicMaterial({ color: colors[i % colors.length] });
      const m = new THREE.Mesh(geo, mat);
      m.position.set(
        v.body.position.x + (Math.random() - 0.5) * 0.6,
        v.body.position.y + 0.6 + Math.random() * 0.4,
        v.body.position.z + (Math.random() - 0.5) * 0.6,
      );
      this.sceneRoot.add(m);
      meshes.push(m);
      velocities.push(new THREE.Vector3(
        (Math.random() - 0.5) * 14,
        4 + Math.random() * 10,
        (Math.random() - 0.5) * 14,
      ));
    }
    this.bursts.push({ meshes, velocities, life: 1.8 });
  }

  private updateBursts(dt: number): void {
    for (let i = this.bursts.length - 1; i >= 0; i--) {
      const b = this.bursts[i];
      b.life -= dt;
      if (b.life <= 0) {
        for (const m of b.meshes) this.sceneRoot.remove(m);
        this.bursts.splice(i, 1);
        continue;
      }
      const fade = Math.max(0, b.life / 1.8);
      for (let j = 0; j < b.meshes.length; j++) {
        const m = b.meshes[j];
        const vel = b.velocities[j];
        m.position.x += vel.x * dt;
        m.position.y += vel.y * dt;
        m.position.z += vel.z * dt;
        vel.y -= 14 * dt; // gravity
        (m.material as THREE.MeshBasicMaterial).opacity = fade;
        (m.material as THREE.MeshBasicMaterial).transparent = true;
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Spawning
  // ─────────────────────────────────────────────────────────────────────────────

  private spawn(x: number, z: number, kind: VehicleKind, hue: number): void {
    const id = this.nextId++;
    const group = new THREE.Group();
    group.position.set(x, 0.7, z);
    this.sceneRoot.add(group);

    const wheels: THREE.Mesh[] = [];
    const headlightMeshes: THREE.Mesh[] = [];

    if (kind === 'quad') {
      // Quad bike / ATV
      const bodyColor = new THREE.Color().setHSL(hue, 0.7, 0.55);
      const chassis = new THREE.Mesh(
        new THREE.BoxGeometry(1.0, 0.4, 1.6),
        toonMat(bodyColor.getHex(), this.grad, {
          emissive: bodyColor.clone().multiplyScalar(0.2).getHex(),
          emissiveIntensity: 0.2,
        }),
      );
      chassis.position.y = 0.45;
      chassis.castShadow = true;
      chassis.receiveShadow = true;
      group.add(chassis);

      // Seat
      const seat = new THREE.Mesh(
        new THREE.BoxGeometry(0.5, 0.18, 0.6),
        toonMat(0x303040, this.grad),
      );
      seat.position.set(0, 0.74, 0.05);
      seat.castShadow = true;
      group.add(seat);

      // Handle bars
      const handle = new THREE.Mesh(
        new THREE.CylinderGeometry(0.04, 0.04, 0.7, 8),
        toonMat(0x202028, this.grad),
      );
      handle.rotation.z = Math.PI / 2;
      handle.position.set(0, 0.92, -0.55);
      group.add(handle);
      const stem = new THREE.Mesh(
        new THREE.CylinderGeometry(0.04, 0.04, 0.5, 6),
        toonMat(0x202028, this.grad),
      );
      stem.position.set(0, 0.72, -0.55);
      group.add(stem);

      // Headlight glow
      const headlight = new THREE.Mesh(
        new THREE.SphereGeometry(0.1, 8, 6),
        new THREE.MeshBasicMaterial({ color: 0xffe6a0 }),
      );
      headlight.position.set(0, 0.82, -0.78);
      group.add(headlight);
      headlightMeshes.push(headlight);

      // 4 wheels
      const wheelGeo = new THREE.CylinderGeometry(0.32, 0.32, 0.22, 14);
      const wheelMat = toonMat(0x1a1a1f, this.grad);
      const positions: [number, number, number][] = [
        [-0.52, 0.32, -0.6],
        [0.52, 0.32, -0.6],
        [-0.52, 0.32, 0.6],
        [0.52, 0.32, 0.6],
      ];
      for (const p of positions) {
        const w = new THREE.Mesh(wheelGeo, wheelMat);
        w.rotation.z = Math.PI / 2;
        w.position.set(p[0], p[1], p[2]);
        w.castShadow = true;
        group.add(w);
        wheels.push(w);
      }
    } else {
      // Truck (pickup style)
      const cabColor = new THREE.Color().setHSL(hue, 0.45, 0.5);
      const cab = new THREE.Mesh(
        new THREE.BoxGeometry(1.6, 1.0, 1.6),
        toonMat(cabColor.getHex(), this.grad, {
          emissive: cabColor.clone().multiplyScalar(0.15).getHex(),
          emissiveIntensity: 0.18,
        }),
      );
      cab.position.set(0, 0.85, -0.4);
      cab.castShadow = true;
      cab.receiveShadow = true;
      group.add(cab);

      const bed = new THREE.Mesh(
        new THREE.BoxGeometry(1.7, 0.55, 1.6),
        toonMat(cabColor.clone().multiplyScalar(0.7).getHex(), this.grad),
      );
      bed.position.set(0, 0.55, 0.9);
      bed.castShadow = true;
      group.add(bed);

      // Windshield
      const wind = new THREE.Mesh(
        new THREE.PlaneGeometry(1.4, 0.65),
        new THREE.MeshStandardMaterial({
          color: 0x99c5e8,
          emissive: 0x6fa4d8,
          emissiveIntensity: 0.4,
          roughness: 0.25,
          metalness: 0.25,
          transparent: true,
          opacity: 0.85,
        }),
      );
      wind.position.set(0, 1.08, -1.2);
      wind.rotation.x = -0.35;
      group.add(wind);

      // Headlights
      for (const hx of [-0.55, 0.55]) {
        const hl = new THREE.Mesh(
          new THREE.SphereGeometry(0.13, 8, 6),
          new THREE.MeshBasicMaterial({ color: 0xfff0c8 }),
        );
        hl.position.set(hx, 0.6, -1.22);
        group.add(hl);
        headlightMeshes.push(hl);
      }

      const wheelGeo = new THREE.CylinderGeometry(0.45, 0.45, 0.32, 16);
      const wheelMat = toonMat(0x1a1a1f, this.grad);
      const positions: [number, number, number][] = [
        [-0.78, 0.45, -0.9],
        [0.78, 0.45, -0.9],
        [-0.78, 0.45, 0.9],
        [0.78, 0.45, 0.9],
      ];
      for (const p of positions) {
        const w = new THREE.Mesh(wheelGeo, wheelMat);
        w.rotation.z = Math.PI / 2;
        w.position.set(p[0], p[1], p[2]);
        w.castShadow = true;
        group.add(w);
        wheels.push(w);
      }
    }

    // Physics body — single box collider (arcade simplification)
    const halfExt =
      kind === 'quad'
        ? new CANNON.Vec3(0.55, 0.45, 0.85)
        : new CANNON.Vec3(0.95, 0.7, 1.3);
    const body = new CANNON.Body({
      mass: kind === 'quad' ? 90 : 220,
      material: this.floorMat,
      linearDamping: 0.32,
      angularDamping: 0.95, // strong rotation damping (we'll set yaw manually)
      fixedRotation: true, // simple arcade — vehicle never tips
    });
    body.addShape(new CANNON.Box(halfExt));
    body.position.set(x, halfExt.y + 0.05, z);
    this.world.addBody(body);

    const seatLocal =
      kind === 'quad' ? new THREE.Vector3(0, 1.1, 0) : new THREE.Vector3(0, 1.45, -0.4);

    const maxHealth = kind === 'quad' ? 200 : 320;

    this.vehicles.push({
      id,
      kind,
      group,
      body,
      wheels,
      speed: 0,
      yaw: Math.random() * Math.PI * 2,
      health: maxHealth,
      maxHealth,
      headlightMeshes,
      hornFlash: 0,
      seatLocal,
      fuel: 100,
      exploded: false,
      boostPadTimer: 0,
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────────────────────────────────────

  /** Returns nearest enterable vehicle within range or null. */
  getEnterableNear(px: number, pz: number): { id: number; kind: VehicleKind; distance: number } | null {
    if (this.playerInVehicleId !== null) return null;
    let best: { id: number; kind: VehicleKind; distance: number } | null = null;
    for (const v of this.vehicles) {
      if (v.exploded) continue;
      const dx = v.body.position.x - px;
      const dz = v.body.position.z - pz;
      const d = Math.hypot(dx, dz);
      if (d > ENTER_RANGE) continue;
      if (!best || d < best.distance) best = { id: v.id, kind: v.kind, distance: d };
    }
    return best;
  }

  enter(id: number): boolean {
    if (this.playerInVehicleId !== null) return false;
    const v = this.vehicles.find((x) => x.id === id);
    if (!v || v.exploded) return false;
    this.playerInVehicleId = id;
    return true;
  }

  exit(): { x: number; y: number; z: number; vx: number; vz: number } | null {
    if (this.playerInVehicleId === null) return null;
    const v = this.vehicles.find((x) => x.id === this.playerInVehicleId);
    this.playerInVehicleId = null;
    this.hideFuelHud();
    if (!v) return null;
    const side = new THREE.Vector3(Math.cos(v.yaw + Math.PI / 2), 0, Math.sin(v.yaw + Math.PI / 2));
    const fwd = new THREE.Vector3(-Math.sin(v.yaw), 0, -Math.cos(v.yaw));
    const boost = 5.5;
    return {
      x: v.body.position.x + side.x * 1.5 + fwd.x * 0.6,
      y: v.body.position.y + 1.5,
      z: v.body.position.z + side.z * 1.5 + fwd.z * 0.6,
      vx: fwd.x * boost,
      vz: fwd.z * boost,
    };
  }

  isPlayerDriving(): boolean {
    return this.playerInVehicleId !== null;
  }

  getCurrentVehicle(): { id: number; kind: VehicleKind } | null {
    if (this.playerInVehicleId === null) return null;
    const v = this.vehicles.find((x) => x.id === this.playerInVehicleId);
    if (!v) return null;
    return { id: v.id, kind: v.kind };
  }

  /** Drive forward camera/seat anchor (world space). */
  getCameraAnchor(): {
    pos: THREE.Vector3;
    yaw: number;
    forward: THREE.Vector3;
  } | null {
    if (this.playerInVehicleId === null) return null;
    const v = this.vehicles.find((x) => x.id === this.playerInVehicleId);
    if (!v) return null;
    const local = v.seatLocal.clone();
    const sin = Math.sin(v.yaw);
    const cos = Math.cos(v.yaw);
    const worldX = v.body.position.x + (cos * local.x - sin * local.z);
    const worldZ = v.body.position.z + (sin * local.x + cos * local.z);
    return {
      pos: new THREE.Vector3(worldX, v.body.position.y + local.y, worldZ),
      yaw: v.yaw,
      forward: new THREE.Vector3(-Math.sin(v.yaw), 0, -Math.cos(v.yaw)),
    };
  }

  /** Fuel percentage (0–100) for the currently driven vehicle. */
  getFuelPct(): number {
    if (this.playerInVehicleId === null) return 100;
    const v = this.vehicles.find((x) => x.id === this.playerInVehicleId);
    return v ? v.fuel : 100;
  }

  /** Apply damage to currently driven vehicle; returns true if vehicle just exploded. */
  damageCurrentVehicle(amount: number): boolean {
    if (this.playerInVehicleId === null) return false;
    const v = this.vehicles.find((x) => x.id === this.playerInVehicleId);
    if (!v || v.exploded) return false;
    v.health = Math.max(0, v.health - amount);
    if (v.health <= 0) {
      this.explodeVehicle(v);
      this.playerInVehicleId = null;
      this.hideFuelHud();
      return true;
    }
    return false;
  }

  /** Apply controls to currently driven vehicle. */
  update(dt: number, controls: VehicleControls): void {
    this.hornCd = Math.max(0, this.hornCd - dt);

    // Update explosion particle bursts
    this.updateBursts(dt);

    // Animate wheel rotation + headlight horn flash for all vehicles
    for (const v of this.vehicles) {
      if (v.exploded) continue;
      const speed = Math.hypot(v.body.velocity.x, v.body.velocity.z);
      const dir = Math.sign(
        Math.cos(v.yaw) * -v.body.velocity.z + Math.sin(v.yaw) * v.body.velocity.x,
      ) || 1;
      const rollAmount = (speed * dt) / 0.3;
      for (const w of v.wheels) {
        w.rotation.x += rollAmount * dir;
      }

      if (v.hornFlash > 0) {
        v.hornFlash = Math.max(0, v.hornFlash - dt);
        const flashOn = Math.sin(v.hornFlash * 42) > 0;
        for (const hl of v.headlightMeshes) {
          const mat = hl.material as THREE.MeshBasicMaterial;
          mat.color.set(flashOn ? 0xffffff : 0xfff0c8);
          mat.opacity = flashOn ? 1 : 0.95;
        }
      } else {
        for (const hl of v.headlightMeshes) {
          const mat = hl.material as THREE.MeshBasicMaterial;
          mat.color.set(0xfff0c8);
        }
      }
    }

    if (this.playerInVehicleId === null) {
      // Sync visual positions to bodies for parked vehicles
      for (const v of this.vehicles) {
        if (v.exploded) continue;
        v.group.position.set(v.body.position.x, v.body.position.y - 0.7, v.body.position.z);
        v.group.rotation.y = v.yaw;
      }
      return;
    }
    const v = this.vehicles.find((x) => x.id === this.playerInVehicleId);
    if (!v) return;

    // If no fuel, cut throttle
    const hasFuel = v.fuel > 0;

    const accel = v.kind === 'quad' ? 22 : 16;
    const boostActive = controls.boost && hasFuel;
    const maxSpeed =
      (v.kind === 'quad' ? 18 : 14) * (boostActive ? 1.55 : 1);
    const turnRate = v.kind === 'quad' ? 2.4 : 1.6;

    let throttle = 0;
    if (controls.forward && hasFuel) throttle += 1;
    if (controls.back && hasFuel) throttle -= 1;

    // Forward direction
    const fwd = new THREE.Vector3(-Math.sin(v.yaw), 0, -Math.cos(v.yaw));

    // Apply throttle as forward velocity
    if (throttle !== 0) {
      const targetSpeed = throttle > 0 ? maxSpeed : -maxSpeed * 0.5;
      // Compute current forward speed (signed)
      const vx = v.body.velocity.x;
      const vz = v.body.velocity.z;
      const fwdSpeed = vx * fwd.x + vz * fwd.z;
      const newFwdSpeed = fwdSpeed + (targetSpeed - fwdSpeed) * Math.min(1, accel * dt * 0.12);
      // Lateral velocity (drift component)
      const latX = vx - fwd.x * fwdSpeed;
      const latZ = vz - fwd.z * fwdSpeed;
      v.body.velocity.x = fwd.x * newFwdSpeed + latX * Math.exp(-2.5 * dt);
      v.body.velocity.z = fwd.z * newFwdSpeed + latZ * Math.exp(-2.5 * dt);
    } else if (controls.brake) {
      v.body.velocity.x *= Math.exp(-6 * dt);
      v.body.velocity.z *= Math.exp(-6 * dt);
    } else {
      // Gentle coast slowdown
      v.body.velocity.x *= Math.exp(-1.1 * dt);
      v.body.velocity.z *= Math.exp(-1.1 * dt);
    }

    // Steering rotates vehicle; only effective when moving (arcade)
    const fwdSpeedNow =
      v.body.velocity.x * fwd.x + v.body.velocity.z * fwd.z;
    if (Math.abs(fwdSpeedNow) > 0.5) {
      const steerSign = fwdSpeedNow > 0 ? 1 : -1;
      let steer = 0;
      if (controls.left) steer += 1;
      if (controls.right) steer -= 1;
      v.yaw += steer * turnRate * dt * steerSign;
    }

    // ── Fuel depletion ────────────────────────────────────────────────────────
    const currentSpeed = Math.hypot(v.body.velocity.x, v.body.velocity.z);
    if (currentSpeed > 0.5) {
      // Base drain: ~1%/s at normal speed, ~2.5%/s at boost
      const drainRate = boostActive ? 2.5 : 1.0;
      // Scale by speed fraction so idling barely drains
      const speedFrac = Math.min(1, currentSpeed / maxSpeed);
      v.fuel = Math.max(0, v.fuel - drainRate * speedFrac * dt);
    }

    // ── Boost pad check ───────────────────────────────────────────────────────
    this.checkBoostPads(v, dt);

    // ── Explosion check ───────────────────────────────────────────────────────
    if (v.health <= 0 && !v.exploded) {
      this.explodeVehicle(v);
      this.playerInVehicleId = null;
      this.hideFuelHud();
      return;
    }

    // Sync visual & cannon yaw
    v.group.position.set(v.body.position.x, v.body.position.y - 0.7, v.body.position.z);
    v.group.rotation.y = v.yaw;
    // Also force body's yaw quaternion so collisions feel oriented (visual cosmetic; fixed rotation, but no-op)
    v.body.quaternion.setFromEuler(0, v.yaw, 0);

    // Update fuel HUD for the driving player
    this.updateFuelHud(v.fuel);
  }

  /** Get current vehicle speed (m/s) for HUD speedometer. */
  getCurrentSpeed(): number {
    if (this.playerInVehicleId === null) return 0;
    const v = this.vehicles.find((x) => x.id === this.playerInVehicleId);
    if (!v) return 0;
    return Math.hypot(v.body.velocity.x, v.body.velocity.z);
  }

  getCurrentHealth(): { health: number; maxHealth: number } | null {
    if (this.playerInVehicleId === null) return null;
    const v = this.vehicles.find((x) => x.id === this.playerInVehicleId);
    if (!v) return null;
    return { health: v.health, maxHealth: v.maxHealth };
  }

  /** Honk horn: headlight flash + brief forward pulse. Returns true if triggered. */
  tryHonk(): boolean {
    if (this.playerInVehicleId === null || this.hornCd > 0) return false;
    const v = this.vehicles.find((x) => x.id === this.playerInVehicleId);
    if (!v) return false;
    this.hornCd = 0.55;
    v.hornFlash = 0.32;
    const fwd = new THREE.Vector3(-Math.sin(v.yaw), 0, -Math.cos(v.yaw));
    const pulse = 4.2;
    v.body.velocity.x += fwd.x * pulse;
    v.body.velocity.z += fwd.z * pulse;
    return true;
  }

  isHornFlashing(): boolean {
    if (this.playerInVehicleId === null) return false;
    const v = this.vehicles.find((x) => x.id === this.playerInVehicleId);
    return !!v && v.hornFlash > 0;
  }

  /** Get vehicle world XZ for minimap. */
  getVehiclePositions(): { x: number; z: number; kind: VehicleKind; driven: boolean }[] {
    return this.vehicles
      .filter((v) => !v.exploded)
      .map((v) => ({
        x: v.body.position.x,
        z: v.body.position.z,
        kind: v.kind,
        driven: v.id === this.playerInVehicleId,
      }));
  }

  dispose(): void {
    for (const v of this.vehicles) {
      this.sceneRoot.remove(v.group);
      this.world.removeBody(v.body);
    }
    this.vehicles.length = 0;
    for (const b of this.bursts) {
      for (const m of b.meshes) this.sceneRoot.remove(m);
    }
    this.bursts.length = 0;
    this.fuelHud?.remove();
    this.fuelHud = null;
  }
}
